import { useTranslation } from "react-i18next";
import { useStatsOverview } from "@/lib/stats";
import type { CountByKey } from "@/lib/stats";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";

function countFor(rows: CountByKey[], key: string): number {
  return rows.find((r) => r.key === key)?.count ?? 0;
}

type Tone = "success" | "warning" | "danger" | "accent" | "neutral";

const TONE_DOT: Record<Tone, string> = {
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  accent: "bg-accent",
  neutral: "bg-fg-muted",
};

function Vital({ label, value, tone }: { label: string; value: number; tone: Tone }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="flex items-start gap-1.5 text-xs leading-snug text-fg-muted">
        <span
          aria-hidden="true"
          className={cn("mt-1 h-1.5 w-1.5 shrink-0 rounded-full", TONE_DOT[tone])}
        />
        <span className="min-w-0">{label}</span>
      </span>
      <span
        className={cn(
          "font-mono text-2xl font-semibold tabular-nums leading-none",
          tone === "danger" && value > 0 && "text-danger",
          tone === "warning" && value > 0 && "text-warning",
        )}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * Live at-a-glance fleet vitals — a single auto-refreshing strip at the top of
 * the dashboard. Carries only the "work is happening" numbers; anything an
 * operator must ACT on (banned, permanent peer-flood, dead proxies) belongs to
 * NeedsAttention, so no number appears in both places. Reuses the already-polling stats/overview query (react-query
 * dedupes the key), so it costs no extra request. The pulsing dot signals to a
 * viewer (e.g. on a sales demo) that the numbers are live, not a snapshot.
 */
export function FleetHealthStrip() {
  const { t } = useTranslation();
  const { data: o, isLoading } = useStatsOverview();

  if (isLoading && !o) {
    return <Skeleton className="h-[92px] rounded-xl" />;
  }
  if (!o) return null;

  const active = countFor(o.accounts.by_status, "active");
  const warming = countFor(o.accounts.by_status, "warming_up");
  const runningCampaigns =
    countFor(o.invites.by_status, "running") + countFor(o.invites.by_status, "queued");

  return (
    <Card className="space-y-3 px-5 py-4">
      <div className="flex items-center gap-2">
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-success" />
        </span>
        <span className="text-sm font-semibold text-fg-primary">
          {t("dashboard.fleet.title")}
        </span>
        <span className="hidden text-xs text-fg-muted sm:inline">
          {t("dashboard.fleet.live")}
        </span>
      </div>

      {/* A grid, not a wrapping flex row: with six vitals the flex row broke
          after the fifth and left one number dangling on its own line. */}
      <div className="grid grid-cols-3 gap-x-6 gap-y-4 sm:grid-cols-6">
        <Vital label={t("dashboard.fleet.active")} value={active} tone="success" />
        <Vital label={t("dashboard.fleet.warming")} value={warming} tone="accent" />
        <Vital label={t("dashboard.fleet.flood")} value={o.accounts.in_flood_wait} tone="warning" />
        <Vital label={t("dashboard.fleet.proxies_alive")} value={o.proxies.alive} tone="success" />
        <Vital
          label={t("dashboard.fleet.campaigns_running")}
          value={runningCampaigns}
          tone="accent"
        />
        {/* Replies are the outcome half of outreach: sends are only work
            done, an incoming message is the first sign it worked. */}
        <Vital label={t("dashboard.fleet.replies_7d")} value={o.inbox.incoming_7d} tone="success" />
      </div>
    </Card>
  );
}
